import React, { useRef, useState } from 'react';

import dayjs from 'dayjs';

import { Badge, Button } from '@/components';
import { FormModal } from '@/design-system/form-modal';
import { Input, InputRoot } from '@/design-system/input';
import { toast } from '@/design-system/toaster';
import {
  shipmentsAPI,
  type ShipmentDocumentType,
  type ShipmentOfficialDocument,
} from '@/services/api/shipments';

import { isPatimbanClient, toNoteKey } from './clientNoteFields';

type DocSpec = { type: ShipmentDocumentType; label: string };

// The required document set is direction-specific (AGL 4W requirement).
const EXPORT_DOCS: DocSpec[] = [
  { type: 'surat_jalan', label: 'Surat Jalan' },
  { type: 'surat_dn', label: 'Surat DN' },
  { type: 'dokumen_timbang_akhir', label: 'Dokumen Hasil Timbang Akhir' },
  { type: 'eir_delivery', label: 'EIR Delivery' },
  { type: 'eir_receiving', label: 'EIR Receiving' },
];
const IMPORT_DOCS: DocSpec[] = [
  { type: 'surat_jalan', label: 'Surat Jalan' },
  { type: 'bl', label: 'BL' },
  { type: 'eir_delivery', label: 'EIR Delivery' },
  { type: 'eir_receiving', label: 'EIR Receiving' },
];

/** PDF only — these are formal scanned documents, not field photos. */
const ACCEPT = 'application/pdf';

/** Every slot label, for documents stored under a slot key on a shipment that
 *  no longer shows the slots (uploaded before the list became per-client). */
const SLOT_LABELS: Record<string, string> = Object.fromEntries(
  [...EXPORT_DOCS, ...IMPORT_DOCS].map((d) => [d.type, d.label])
);

/** Server bounds for an ad-hoc document (official-documents.service.ts). */
export const AD_HOC_DOC_PREFIX = 'doc_';
export const AD_HOC_DOC_LABEL_MAX = 60;
export const AD_HOC_DOC_MAX = 20;

/** The key the server stores an ad-hoc document under ('' when the name has
 *  no letter or digit — the server refuses those). */
export const adHocDocKey = (label: string): string => {
  const snake = toNoteKey(label);
  return snake ? `${AD_HOC_DOC_PREFIX}${snake}` : '';
};

/** What to call a stored document: the name ops typed, the slot's label, or the key. */
export const documentLabel = (
  key: string,
  doc: ShipmentOfficialDocument
): string => doc.label ?? SLOT_LABELS[key] ?? key;

interface DokumenResmiSectionProps {
  shipmentId: string;
  /** Client display name — Patimban gets the fixed slots, others add their own. */
  clientName?: string | null;
  /** Shipment direction — picks the required document set. */
  exim?: string | null;
  documents?: Record<string, ShipmentOfficialDocument> | null;
  /** Called with the merged map after a successful upload+save. */
  onUpdated: (documents: Record<string, ShipmentOfficialDocument>) => void;
}

/**
 * Ops-attested official documents for a DIRECT_4W shipment
 * (PLAN-SHIPMENT-DOCS-DYNAMIC). Patimban keeps its fixed slots; every other
 * client names and uploads its own documents, which lock once uploaded.
 * Driver field photos stay in the timeline as field proof, separate from these.
 */
const DokumenResmiSection: React.FC<DokumenResmiSectionProps> = (props) =>
  isPatimbanClient(props.clientName) ? (
    <SlotDocuments {...props} />
  ) : (
    <AdHocDocuments {...props} />
  );

/**
 * Patimban: one fixed slot per required type (Surat Jalan, EIR, …). Each
 * uploads one file to GCS then commits its URL onto the shipment; these feed
 * the export's Dok columns and the client-facing view. A reupload replaces.
 */
const SlotDocuments: React.FC<DokumenResmiSectionProps> = ({
  shipmentId,
  exim,
  documents,
  onUpdated,
}) => {
  const [busy, setBusy] = useState<ShipmentDocumentType | null>(null);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const docs = exim === 'IMPORT' ? IMPORT_DOCS : EXPORT_DOCS;

  const handleFile = async (type: ShipmentDocumentType, file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Hanya PDF', { description: 'Dokumen resmi harus berupa PDF.' });
      return;
    }
    setBusy(type);
    try {
      const url = await shipmentsAPI.uploadDocument(shipmentId, file);
      const merged = await shipmentsAPI.saveDocument(shipmentId, {
        docType: type,
        url,
        fileName: file.name,
        contentType: file.type || null,
      });
      onUpdated(merged);
      toast.success('Dokumen resmi tersimpan');
    } catch (err) {
      toast.error('Gagal upload dokumen', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section>
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
        Dokumen Resmi
      </p>

      <div className="rounded-gsm border border-gray-200 bg-white">
        {docs.map(({ type, label }, i) => {
          const doc = documents?.[type];
          return (
            <div
              key={type}
              className={`flex items-center gap-3 px-4 py-3 ${
                i > 0 ? 'border-t border-gray-100' : ''
              }`}
            >
              <i className="ri-file-text-line text-[20px] text-gray-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold tracking-tight text-gray-900">
                  {label}
                </p>
                {doc ? (
                  <p className="mt-0.5 truncate text-[11px] text-gray-500">
                    oleh {doc.uploadedBy} ·{' '}
                    {dayjs(doc.uploadedAt).format('YYYY-MM-DD HH:mm')}
                  </p>
                ) : (
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    Belum diunggah
                  </p>
                )}
              </div>

              <input
                ref={(el) => {
                  inputs.current[type] = el;
                }}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(type, f);
                  e.target.value = '';
                }}
              />

              {doc ? (
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="max-w-[180px] truncate font-mono text-[12px] text-accent hover:underline"
                      title={doc.filename}
                    >
                      {doc.filename}
                    </a>
                    <Badge tone="success">Resmi</Badge>
                  </div>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => inputs.current[type]?.click()}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-500 underline underline-offset-2 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy === type && (
                      <i className="ri-loader-4-line animate-spin text-[12px] no-underline" />
                    )}
                    {busy === type ? 'Mengunggah…' : 'Upload ulang'}
                  </button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="small"
                  disabled={busy !== null}
                  onClick={() => inputs.current[type]?.click()}
                >
                  <i
                    className={`mr-1.5 text-[14px] ${
                      busy === type
                        ? 'ri-loader-4-line animate-spin'
                        : 'ri-upload-2-line'
                    }`}
                  />
                  {busy === type ? 'Mengunggah…' : 'Upload dokumen'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
        PDF saja. Dokumen resmi tampil ke client (Dash Link) &amp; masuk{' '}
        <strong>kolom Dok</strong> di export CSV. Foto driver tetap di timeline
        sebagai bukti lapangan.
      </p>
    </section>
  );
};

/**
 * Every other client: the documents already on the shipment (each locked —
 * no reupload, no delete) and a row to add one more, named by ops. The name
 * becomes the key `doc_<name>`, so a name can be used once per shipment; the
 * server enforces the lock and the cap, this only avoids a wasted upload.
 */
const AdHocDocuments: React.FC<DokumenResmiSectionProps> = ({
  shipmentId,
  documents,
  onUpdated,
}) => {
  const [name, setName] = useState('');
  const [pending, setPending] = useState<{ label: string; file: File } | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  const stored = Object.entries(documents ?? {}).sort(([, a], [, b]) =>
    (a.uploadedAt ?? '').localeCompare(b.uploadedAt ?? '')
  );
  const adHocCount = stored.filter(([key]) =>
    key.startsWith(AD_HOC_DOC_PREFIX)
  ).length;
  const label = name.trim().replace(/\s+/g, ' ');
  const key = adHocDocKey(label);
  const taken = key !== '' && !!documents?.[key];
  const full = adHocCount >= AD_HOC_DOC_MAX;
  const canPick = !busy && !full && key !== '' && !taken;

  const pickFile = (file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Hanya PDF', { description: 'Dokumen resmi harus berupa PDF.' });
      return;
    }
    setPending({ label, file });
  };

  const upload = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const url = await shipmentsAPI.uploadDocument(shipmentId, pending.file);
      const merged = await shipmentsAPI.saveDocument(shipmentId, {
        label: pending.label,
        url,
        fileName: pending.file.name,
        contentType: pending.file.type || null,
      });
      onUpdated(merged);
      setName('');
      toast.success(`${pending.label} tersimpan`);
      setPending(null);
    } catch (err) {
      toast.error('Gagal upload dokumen', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="dokumen-resmi-adhoc">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
        Dokumen Resmi
      </p>

      <div className="rounded-gsm border border-gray-200 bg-white">
        {stored.length === 0 && (
          <p className="px-4 py-3 text-[12px] text-gray-400">
            Belum ada dokumen.
          </p>
        )}
        {stored.map(([docKey, doc], i) => (
          <div
            key={docKey}
            data-testid="adhoc-doc-row"
            className={`flex items-center gap-3 px-4 py-3 ${
              i > 0 ? 'border-t border-gray-100' : ''
            }`}
          >
            <i className="ri-file-text-line text-[20px] text-gray-400" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold tracking-tight text-gray-900">
                {documentLabel(docKey, doc)}
              </p>
              <p className="mt-0.5 truncate text-[11px] text-gray-500">
                oleh {doc.uploadedBy} ·{' '}
                {dayjs(doc.uploadedAt).format('YYYY-MM-DD HH:mm')}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="max-w-[180px] truncate font-mono text-[12px] text-accent hover:underline"
                  title={doc.filename}
                >
                  {doc.filename}
                </a>
                <Badge tone="success">Resmi</Badge>
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                <i className="ri-lock-line text-[12px]" />
                Terkunci
              </span>
            </div>
          </div>
        ))}

        <div className="border-t border-gray-100 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <i className="ri-file-add-line text-[20px] text-gray-400" />
            <InputRoot size="md" className="w-[240px]">
              <Input
                aria-label="Nama dokumen"
                className="min-w-0 w-full"
                value={name}
                maxLength={AD_HOC_DOC_LABEL_MAX}
                placeholder="Nama dokumen, mis. Surat Jalan"
                disabled={busy || full}
                onChange={(e) => setName(e.target.value)}
              />
            </InputRoot>
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              className="hidden"
              data-testid="adhoc-doc-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickFile(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              size="small"
              disabled={!canPick}
              onClick={() => input.current?.click()}
            >
              <i className="ri-upload-2-line mr-1.5 text-[14px]" />
              Pilih PDF
            </Button>
          </div>
          {full ? (
            <p className="ml-8 mt-2 text-[11px] text-gray-500">
              Maksimal {AD_HOC_DOC_MAX} dokumen per shipment.
            </p>
          ) : taken ? (
            <p className="ml-8 mt-2 text-[11px] text-red-600">
              Nama ini sudah dipakai dokumen di atas. Pakai nama lain.
            </p>
          ) : label !== '' && key === '' ? (
            <p className="ml-8 mt-2 text-[11px] text-red-600">
              Nama dokumen harus memuat huruf atau angka.
            </p>
          ) : null}
        </div>
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
        PDF saja. Dokumen tampil ke client (Dash Link). Setelah diunggah,
        dokumen <strong>terkunci</strong>: tidak bisa dihapus atau diganti.
      </p>

      {/* FormModal, not the @/components Modal: this section lives INSIDE the
          shipment detail panel, itself a FormModal (see InformasiTambahanSection). */}
      <FormModal
        open={pending !== null}
        onClose={() => setPending(null)}
        icon={<i className="ri-lock-line" />}
        iconTone="warning"
        title={`Unggah ${pending?.label ?? ''}?`}
        subtitle="Dokumen ini tidak bisa dihapus atau diganti setelah diunggah."
        size="sm"
        closeOnBackdropClick={!busy}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="white"
              disabled={busy}
              onClick={() => setPending(null)}
            >
              Batal
            </Button>
            <Button disabled={busy} onClick={upload}>
              {busy ? 'Mengunggah…' : 'Unggah & kunci'}
            </Button>
          </div>
        }
      >
        <p className="font-mono text-[12px] text-gray-700">
          {pending?.file.name}
        </p>
      </FormModal>
    </section>
  );
};

export default DokumenResmiSection;
